import config from "../config.json"

import FighterInterface, { SerializedFighterType } from "./FighterInterface"
import Status from "./Status"

export type SerializedGame = {
  id: string
  current: SerializedFighterType
  next: SerializedFighterType[]
  enemiesQueue: number[]
  time: number
  level: number
  status: Status
  defeatReason: string | undefined
  initialValue: number
}

export default class Game {
  public readonly me: FighterInterface
  private readonly _enemies: FighterInterface[]
  constructor(
    public readonly id: string,
    me: FighterInterface,
    enemies: FighterInterface[],
    public readonly enemiesQueue: number[],
    public readonly time: number,
    public readonly level: number,
    public readonly initialValue: number,
    public readonly status: Status = "idle",
    public readonly endGameReason: string | undefined = undefined,
  ) {
    this.me = me.clone()
    this._enemies = enemies.map(
      (value: FighterInterface): FighterInterface => value.clone(),
    )
  }

  public get enemies(): FighterInterface[] {
    return this._enemies.slice(0, config.maxEnemies)
  }

  public setTime(time: number): Game {
    const cloning: Game = this.clone()
    Object.assign(cloning, { time })

    return cloning
  }

  public addTime(time: number): Game {
    return this.setTime(this.time + time)
  }

  public addNextEnemy(next: FighterInterface): Game {
    return new Game(
      this.id,
      this.me,
      [...this._enemies, next],
      this.enemiesQueue,
      this.time,
      this.level,
      this.initialValue,
      this.status,
      this.endGameReason,
    )
  }

  public setStatus(status: Status, reason?: string): Game {
    return new Game(
      this.id,
      this.me,
      this._enemies,
      this.enemiesQueue,
      this.time,
      this.level,
      this.initialValue,
      status,
      reason ?? this.endGameReason,
    )
  }

  public fight(enemy: FighterInterface): Game {
    return new Game(
      this.id,
      this.me.setStatus("fighting"),
      this._enemies.map((e: FighterInterface): FighterInterface => {
        if (e.id !== enemy.id) {
          return e
        }

        return enemy.setStatus("fighting")
      }),
      this.enemiesQueue,
      this.time,
      this.level,
      this.initialValue,
      this.status,
      this.endGameReason,
    )
  }

  public endFight(won: boolean): Game {
    return new Game(
      this.id,
      this.me.setStatus(won ? "idle" : "dying"),
      this._enemies.map((e: FighterInterface): FighterInterface => {
        if (e.status !== "fighting") {
          return e
        }

        return e.setStatus(won ? "dying" : "idle")
      }),
      this.enemiesQueue,
      this.time,
      this.level,
      this.initialValue,
      this.status,
      this.endGameReason,
    )
  }

  public setIdle(): Game {
    return new Game(
      this.id,
      this.me.setStatus(this.me.status === "dying" ? "dead" : "idle"),
      this._enemies.map((e: FighterInterface): FighterInterface => {
        return e.setStatus(e.status === "dying" ? "dead" : "idle")
      }),
      this.enemiesQueue,
      this.time,
      this.level,
      this.initialValue,
      "idle",
      this.endGameReason,
    )
  }

  public defeat(reason: string): Game {
    return this.setStatus("defeat", reason)
  }

  get enemyFighter(): FighterInterface | undefined {
    return this.enemies.find(
      (f: FighterInterface): boolean => f.status === "fighting",
    )
  }

  public toJSON(): SerializedGame {
    return {
      id: this.id,
      current: this.me.toJSON(),
      next: this._enemies.map(
        (enemy: FighterInterface): SerializedFighterType => enemy.toJSON(),
      ),
      enemiesQueue: this.enemiesQueue,
      time: this.time,
      level: this.level,
      status: this.status,
      defeatReason: this.endGameReason,
      initialValue: this.initialValue,
    }
  }

  private clone(): Game {
    return new Game(
      this.id,
      this.me,
      this._enemies,
      this.enemiesQueue,
      this.time,
      this.level,
      this.initialValue,
      this.status,
      this.endGameReason,
    )
  }
}
